import { Inject } from '@odinjs/odin';
import { FetchAPI, ModalProps, ModalService, TextAPI } from '@philips-internal/oswald-ui-components';
import ModalButtonType from '@philips-internal/oswald-ui-constants/lib/modal-button-type';
import Controller, { OnAfterFetch, OnClick } from '@philips-internal/oswald-ui-core/lib/client';
import { FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { AuthenticationService, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils/lib';
import { ApplicationModulesIds } from '../../app/module-registry/application-modules';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { LastUserAccess } from '../../interfaces/last-user-acccess';
import { cast } from '../../utils/cast-helpers';
import DateFormatterUtil from '../../utils/date-utils/date-formatter-util';
import { showToastNotificationError } from '../../utils/toast-notification-utils';
import BaseController from '../BaseController';
import './index.css';

interface State {
  isNavigationToModal: boolean;
}

@Controller({ component: 'layout-profile', fragment: 'profile' })
export default class ProfileController extends BaseController {

  private lastUserAccess!: LastUserAccess;

  @Inject('text-last-login')
    lastloginText!: TextAPI;

  @Inject('text-last-login-mobile')
    lastloginTextMobile!: TextAPI;

  @Inject('fetch-last-access-date')
    fetchLastAccessDate!: FetchAPI;

  private state: State = {
    isNavigationToModal: false,
  }

  @OnAfterFetch({ target: 'fetch-last-access-date' })
  onMountFetchLastAccessDate(fetchEvent: FetchEvent): void {
    this.lastUserAccess = cast(fetchEvent.data);
    const dateLastAccess = DateFormatterUtil.short(this.lastUserAccess.dtLastAccess.toString());

    this.lastloginText.setValue(`${MessageService.translate('patient.profile.last.login')}: ${dateLastAccess}`);
  }

  @OnAfterFetch({ target: 'fetch-last-access-date-mobile' })
  onMountFetchLastAccessDateMobile(fetchEvent: FetchEvent): void {
    this.lastUserAccess = cast(fetchEvent.data);
    const dateLastAccess = DateFormatterUtil.short(this.lastUserAccess.dtLastAccess.toString());

    this.lastloginTextMobile.setValue(`${MessageService.translate('patient.profile.last.login')}: ${dateLastAccess}`);
  }

  @OnClick({ target: 'link-show-guidance-and-regulatory-modal' })
  onClickShowModalGuidanceAndRegualtory(): void {
    this.state.isNavigationToModal = true;
    this.historyService.push(APPLICATION_ROUTES.GUIDANCE_AND_REGULATORY);
  }

  @OnClick({ target: 'link-change-password' })
  onClickChangePassword(): void {
    this.state.isNavigationToModal = true;
    this.historyService.push(APPLICATION_ROUTES.PROFILE_CHANGE_PASSWORD);
  }

  @OnClick({ target: 'link-privacy' })
  onClickTermsOfUse(): void {
    //applicationContext.closeModule(ApplicationModulesIds.PROFILE);
    ModalService.openFullscreen({
      closeable: true,
      fragment: 'profile-privacy',
      noPadding: true,
      onBeforeClose: () => {
        applicationContext.openModule(ApplicationModulesIds.PROFILE);
      },
      title: 'patient.label.terms-of-use',
    });
  }

  @OnClick({ target: 'link-close-my-login-account' })
  onCloseAccountClick(): void {

    const modalProps: ModalProps = {
      closeByDocument: false,
      closeByEscape: false,
      dialogDescription: MessageService.translate('patient.confirm-close-account'),
      dockButtons: [
        {
          action: () => {},
          title: MessageService.translate('patient.cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: this.onSubmitCloseAccount,
          title: MessageService.translate('patient.continue'),
          type: ModalButtonType.SUCCESS
        }
      ],
      focusOnOpen: true,
      title: MessageService.translate('patient.user-confirmation'),
      uuid: generateUUID(),
    };

    ModalService.openDialog(modalProps);
  }

  onSubmitCloseAccount(): void {
    HttpService.post(APPLICATION_ENDPOINTS.POST_CLOSE_ACCOUNT, {})
      .then(() =>
        AuthenticationService.logout().then(() => {
          AuthenticationService.redirectToLogin();
        }))
      .catch((error) => showToastNotificationError(error?.message));
  }
}
